import { closestCenter, DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, List, Space } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import VSpace from '../common/VSpace';
import { DatacenterReportType, getDateFromDateRange, ReportFilterViewModel } from './datacenterModel';
import { useSortFiltersMutation } from './datacenterQueries';
import { InvoiceReportFilters } from './InvoiceReport/InvoiceReport';
import { ItemsReportFilters } from './ItemsReport/ItemsReport';
import { ProposalsReportFilters } from './ProposalsReport/ProposalsReport';

type FilterItem = ProposalsReportFilters | ItemsReportFilters | InvoiceReportFilters

type Props = {
  filtersList: ReportFilterViewModel[]
  onFilterSelect: (filters: FilterItem) => void
  onDeleteFilters: (filterIds: string[]) => void
  renderFilterDetails: (item: ReportFilterViewModel) => React.ReactNode
  reportType: DatacenterReportType
}

type SortableItemProps = {
  item: ReportFilterViewModel;
  onClick: (filters: FilterItem) => void;
  showCheckboxes: boolean;
  selectedFilters: string[];
  handleCheckboxChange: (filterId: string, checked: boolean) => void;
  renderFilterDetails: (item: ReportFilterViewModel) => React.ReactNode;
};

const SortableItem = ({ item, onClick, showCheckboxes, selectedFilters, handleCheckboxChange, renderFilterDetails }: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleClick = () => {
    const isDateRangeCustom = item.filters.dateRange === 'custom'
    const dateRange = getDateFromDateRange(item.filters.dateRange)

    const filters: FilterItem = {
      ...item.filters,
      startDate: isDateRangeCustom ? dayjs(item.filters.startDate) : dateRange[0],
      endDate: isDateRangeCustom ? dayjs(item.filters.endDate) : dateRange[1]
    }

    onClick(filters)
  }

  return (
    <List.Item
      ref={setNodeRef}
      style={{
        ...style,
        border: '1px solid #f0f0f0',
        borderRadius: 4,
        marginBottom: 8,
        padding: 10,
        cursor: 'pointer',
        transition: 'background-color 0.3s',
        display: 'flex',
        alignItems: 'center',
      }}
      onClick={handleClick}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
    >
      <Space>
        {showCheckboxes ? (
          <Checkbox
            checked={selectedFilters.includes(item.id)}
            onChange={(e) => handleCheckboxChange(item.id, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        ) : <FontAwesomeIcon 
          icon={faGripVertical} 
          style={{ cursor: 'grab' }}
          onMouseDown={(e) => e.currentTarget.style.cursor = 'grabbing'}
          onMouseUp={(e) => e.currentTarget.style.cursor = 'grab'}
          {...attributes}
          {...listeners}
        />}
        {renderFilterDetails(item)}
      </Space>
    </List.Item>
  );
};

const ReportSavedFiltersList = ({ filtersList, onFilterSelect, onDeleteFilters, renderFilterDetails, reportType }: Props) => {
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [showCheckboxes, setShowCheckboxes] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const items = filtersList.map(filter => filter.id);
  const sortFiltersMutation = useSortFiltersMutation();

  const handleCheckboxChange = (filterId: string, checked: boolean) => {
    setSelectedFilters(prevSelectedFilters =>
      checked ? [...prevSelectedFilters, filterId] : prevSelectedFilters.filter(id => id !== filterId)
    );
  };

  const handleGlobalCheckboxChange = (checked: boolean) => {
    if (checked) {
      setSelectedFilters(filtersList.map(filter => filter.id));
    } else {
      setSelectedFilters([]);
    }
  };

  const handleDelete = () => {
    onDeleteFilters(selectedFilters);
    setSelectedFilters([]);
    setShowCheckboxes(false);
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id.toString());
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = items.indexOf(active.id.toString())
      const newIndex = over ? items.indexOf(over.id.toString()) : -1

      const newItems = arrayMove(items, oldIndex, newIndex)
      const newFiltersList = newItems.map(id => filtersList.find(filter => filter.id === id)!)
      sortFiltersMutation.mutate({
        filterIds: newFiltersList.map((filter, index) => ({ [filter.id]: index })),
        filtersList: newFiltersList,
        reportType: reportType
      })
    }
  };

  return (
    <DndContext collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <VSpace>
          <Space wrap>
            {
              showCheckboxes &&
              <Button
                type="primary"
                danger
                onClick={handleDelete}
                disabled={selectedFilters.length === 0}
              >
                Confirm Delete
              </Button>
            }
            <Button
              type='default'
              onClick={() => setShowCheckboxes(!showCheckboxes)}
            >
              {showCheckboxes ? 'Cancel' : 'Delete Filters'}
            </Button>
            {showCheckboxes && (
              <Checkbox
                checked={selectedFilters.length === filtersList.length}
                onChange={(e) => handleGlobalCheckboxChange(e.target.checked)}
              >
                Select All
              </Checkbox>
            )}
          </Space>
          <List
            style={{ height: 900, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: '#888 #f0f0f0' }}
            dataSource={items}
            renderItem={(id: string) => {
              const item = filtersList.find(filter => filter.id === id)!;
              return (
                <SortableItem
                  key={id}
                  item={item}
                  onClick={onFilterSelect}
                  showCheckboxes={showCheckboxes}
                  selectedFilters={selectedFilters}
                  handleCheckboxChange={handleCheckboxChange}
                  renderFilterDetails={renderFilterDetails}
                />
              );
            }}
          />
        </VSpace>
      </SortableContext>
      <DragOverlay style={{ cursor: 'grabbing' }} dropAnimation={null}>
        {activeId ? (
          <SortableItem
            item={filtersList.find(filter => filter.id === activeId)!}
            onClick={onFilterSelect}
            showCheckboxes={showCheckboxes}
            selectedFilters={selectedFilters}
            handleCheckboxChange={handleCheckboxChange}
            renderFilterDetails={renderFilterDetails}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default ReportSavedFiltersList;